/**
 * El protocolo de tiempo real (§12).
 *
 * Vive aquí, una sola vez, porque cliente y servidor no pueden desincronizarse si no existen dos
 * definiciones. Una copia divergente del esquema de un evento se manifiesta como un fallo en
 * producción con cuarenta alumnos delante, no como un error de compilación (principio II).
 *
 * **El cliente nunca envía marcas de tiempo.** Ningún mensaje de ida las lleva, y no es un olvido:
 * es el principio I. El cronómetro del alumno será una animación sincronizada con la hora que
 * emite el servidor.
 */
import { z } from 'zod';
import { localeSchema } from '../session-contract.js';
import { roomSettingsSchema } from '../room-settings.js';
// ---------------------------------------------------------------------------
// Códigos de cierre (§12.4)
// ---------------------------------------------------------------------------
/**
 * Todos en el rango 4000-4999, el único que el navegador entrega intacto.
 *
 * Por debajo de 4000 los genera la pila y no dicen nada útil: el 1006 que ve el cliente cuando se
 * corta la wifi no distingue una caída de red de un rechazo.
 */
export const CLOSE_CODES = {
    /** Otra conexión con la misma cuenta ha tomado el relevo. */
    sessionReplaced: 4001,
    /** No llegó el saludo a tiempo. */
    helloTimeout: 4002,
    /** El cliente ha hablado fuera de turno o ha mandado basura. */
    protocolError: 4003,
    /** Token caducado o mal firmado. */
    badToken: 4004,
    /**
     * Firma válida, pero sin perfil en Clica.
     *
     * Merece su propio código: `auth.users` es global a la instancia, así que alguien de otro
     * proyecto alojado presenta un token perfecto contra el mismo secreto. Se arregla dando de alta
     * el perfil, no refrescando el token.
     */
    noProfile: 4005,
    /** Acceso retirado. */
    banned: 4006,
    /** Expulsado de la sala por el profesor. */
    kicked: 4007,
    /** Fallo temporal del servidor: reintentar con espera creciente. */
    temporary: 4010,
};
/** Los que no tiene sentido reintentar: insistir no arregla nada. */
export const FINAL_CLOSE_CODES = [
    CLOSE_CODES.sessionReplaced,
    CLOSE_CODES.protocolError,
    CLOSE_CODES.noProfile,
    CLOSE_CODES.banned,
    CLOSE_CODES.kicked,
];
// ---------------------------------------------------------------------------
// Cliente → Servidor
// ---------------------------------------------------------------------------
/**
 * El saludo. Va en el primer mensaje y **no en la URL**, para que el token no acabe en los
 * registros del proxy.
 */
export const helloSchema = z.object({
    type: z.literal('hello'),
    token: z.string().min(1),
    /** En qué sala se quiere entrar. Ausente si solo se quiere una conexión, sin sala. */
    pin: z
        .string()
        .regex(/^[0-9]{4}$/)
        .optional(),
    /**
     * Identifica **el dispositivo**, no la conexión.
     *
     * Es la pieza que distingue «he recargado la página» de «he abierto mi cuenta en otro sitio»,
     * que vistas desde el servidor son la misma cosa: una conexión nueva con la misma identidad. Lo
     * genera el cliente una vez y lo guarda; sobrevive a la recarga, que es el caso normal.
     */
    deviceId: z.string().min(8).max(64),
});
export const joinSchema = z.object({
    type: z.literal('join'),
    pin: z.string().regex(/^[0-9]{4}$/),
    alias: z.string().optional(),
    /** El idioma se usa para elegir en qué lengua se le habla, no para nada más. */
    locale: localeSchema.optional(),
});
export const hostToggleAliasSchema = z.object({
    type: z.literal('host:toggle_alias'),
    enabled: z.boolean(),
});
export const hostKickSchema = z.object({
    type: z.literal('host:kick'),
    participantId: z.uuid(),
});
export const hostStartSchema = z.object({ type: z.literal('host:start') });
export const hostEndSchema = z.object({ type: z.literal('host:end') });
/**
 * Los ajustes de esta sala, cambiados con la clase delante.
 *
 * Llega el objeto entero y no un parche: así el servidor no tiene que fusionar nada, y dos
 * pestañas del profesor abiertas a la vez no se pisan con dos parches parciales. La validación es
 * la misma que se guarda, y vive una sola vez (principio II).
 */
export const hostSettingsSchema = z.object({
    type: z.literal('host:settings'),
    settings: roomSettingsSchema,
});
/**
 * La respuesta de un alumno.
 *
 * **No lleva ninguna marca de tiempo, y esa ausencia es el principio I.** El instante que cuenta es
 * el de llegada al servidor. Lleva el identificador de la pregunta para que una respuesta que llega
 * tarde —a la pregunta anterior— se pueda rechazar en lugar de aplicarse a la que está abierta.
 */
export const answerSchema = z.object({
    type: z.literal('answer'),
    questionId: z.string().min(1),
    optionId: z.string().min(1),
});
/**
 * La respuesta al tic.
 *
 * Devuelve el identificador y **nada más**: el servidor mide el ida y vuelta con su propio reloj. Si
 * el cliente enviara su hora, el principio I se rompería y falsearla daría puntos.
 */
export const pongSchema = z.object({
    type: z.literal('pong'),
    tickId: z.number().int().nonnegative(),
});
export const hostPauseSchema = z.object({ type: z.literal('host:pause') });
export const hostResumeSchema = z.object({ type: z.literal('host:resume') });
export const hostCloseQuestionSchema = z.object({ type: z.literal('host:close_question') });
export const hostNextSchema = z.object({ type: z.literal('host:next') });
export const clientMessageSchema = z.discriminatedUnion('type', [
    helloSchema,
    joinSchema,
    answerSchema,
    pongSchema,
    hostToggleAliasSchema,
    hostKickSchema,
    hostStartSchema,
    hostPauseSchema,
    hostResumeSchema,
    hostCloseQuestionSchema,
    hostNextSchema,
    hostEndSchema,
    hostSettingsSchema,
]);
// ---------------------------------------------------------------------------
// Servidor → Cliente
// ---------------------------------------------------------------------------
/**
 * Los errores del protocolo.
 *
 * Viajan como **clave de traducción**, no como frase: el idioma lo resuelve el cliente, y el
 * proyector del aula y el móvil del alumno pueden estar en lenguas distintas (principio V). Es la
 * misma forma que usan las rutas HTTP: una sola forma de error en todo el producto.
 */
export const GAME_ERROR_KEYS = [
    'game.error.pinNotFound',
    'game.error.gameStarted',
    /** El correo con el que se ha entrado no es de un dominio admitido en ESTA sala. */
    'game.error.domainNotAllowed',
    'game.error.kicked',
    'game.error.notHost',
    'game.error.aliasRejected',
    'game.error.tooManyAttempts',
    'game.error.quizNotPlayable',
    'game.error.temporary',
    'game.error.questionClosed',
    'game.error.alreadyAnswered',
    'game.error.gamePaused',
    'game.error.unknownOption',
];
/**
 * Cuánto se espera al saludo antes de cerrar.
 *
 * Un cliente legítimo lo manda en el mismo turno en que se abre el socket: ya tiene el token en
 * memoria. Los diez segundos no son holgura para él, son holgura para el móvil que despierta de
 * suspensión. Y por arriba acotan lo que cuesta un socket anónimo: ninguno vive más sin
 * identificarse, así que no se acumulan.
 */
export const HELLO_TIMEOUT_MS = 10_000;
/** Cada cuánto late la conexión. Muy por debajo del tiempo de inactividad del proxy. */
export const HEARTBEAT_MS = 15_000;
/** Tamaño máximo de un mensaje. Los de este protocolo son de decenas de bytes. */
export const MAX_MESSAGE_BYTES = 8 * 1024;
//# sourceMappingURL=protocol.js.map